import React, { useState, useCallback } from 'react';
import { buildFileTree } from '../vibeCodingUtils';
import ExplorerContextMenu from './ExplorerContextMenu';

const TreeNode = ({
  node,
  depth,
  selectedPath,
  onSelectFile,
  expanded,
  toggleFolder,
  onContextMenu,
}) => {
  const paddingLeft = 10 + depth * 14;
  const isFolder = node.type === 'folder';
  const isOpen = isFolder && expanded[node.path];
  const isActive = !isFolder && node.path === selectedPath;

  const label = node.name;

  const onClick = () => {
    if (isFolder) {
      toggleFolder(node.path);
      return;
    }
    onSelectFile(node.path);
  };

  return (
    <div className="vibe-tree-node-block">
      <button
        type="button"
        className={`vibe-tree-node btn btn-link text-left text-decoration-none w-100 rounded-0 border-0 py-1 px-0 ${
          isActive ? 'active' : ''
        }`}
        style={{ paddingLeft }}
        title={node.path}
        onClick={onClick}
        onContextMenu={(e) => {
          if (onContextMenu) onContextMenu(e, node);
        }}
      >
        <span className="vibe-tree-chevron d-inline-block text-center" style={{ width: 16 }}>
          {isFolder ? (isOpen ? '▾' : '▸') : ''}
        </span>
        <span className="mr-1" aria-hidden>
          {isFolder ? (
            <i className={`wb ${isOpen ? 'webconnect-folder-open' : 'webconnect-folder'}`} />
          ) : (
            <i className="wb webconnect-file-code" />
          )}
        </span>
        <span className="text-truncate">{label}</span>
      </button>

      {isFolder && isOpen && node.children && node.children.length
        ? node.children.map((child) => (
            <TreeNode
              key={`${child.type}:${child.path}`}
              node={child}
              depth={depth + 1}
              selectedPath={selectedPath}
              onSelectFile={onSelectFile}
              expanded={expanded}
              toggleFolder={toggleFolder}
              onContextMenu={onContextMenu}
            />
          ))
        : null}
    </div>
  );
};

const EditorSidebar = ({
  vibe,
  selectedPath,
  onSelectFile,
  onCollapse,
  explorerWidth,
  onExplorerWidthChange,
  onCreateFile,
  onCreateFolder,
  onRename,
  onDelete,
}) => {
  const raw = vibe && vibe.workspaceFiles;
  const workspaceFiles = raw && typeof raw === 'object' ? raw : {};
  const roots = buildFileTree(workspaceFiles);
  const width = explorerWidth || 260;

  const [menu, setMenu] = useState(null);
  const openContextMenu = (event, node) => {
    event.preventDefault();
    event.stopPropagation();
    setMenu({ x: event.clientX, y: event.clientY, node });
  };
  const closeContextMenu = () => setMenu(null);

  const buildMenuItems = (node) => {
    if (!node) return [];

    const isFolder = node.type === 'folder';
    const parentDir = isFolder
      ? node.path
      : node.path.includes('/')
      ? node.path.split('/').slice(0, -1).join('/')
      : '';

    const items = [];

    if (isFolder) {
      items.push(
        {
          key: 'new-file',
          label: 'New File',
          onClick: () => onCreateFile && onCreateFile({ parentPath: node.path }),
        },
        {
          key: 'new-folder',
          label: 'New Folder',
          onClick: () => onCreateFolder && onCreateFolder({ parentPath: node.path }),
        },
        { key: 'd1', divider: true },
      );
    } else {
      items.push(
        {
          key: 'new-file-sibling',
          label: 'New File',
          onClick: () => onCreateFile && onCreateFile({ parentPath: parentDir }),
        },
        {
          key: 'new-folder-sibling',
          label: 'New Folder',
          onClick: () => onCreateFolder && onCreateFolder({ parentPath: parentDir }),
        },
        { key: 'd1', divider: true },
      );
    }

    items.push(
      {
        key: 'rename',
        label: 'Rename',
        onClick: () => onRename && onRename({ node }),
      },
      {
        key: 'delete',
        label: 'Delete',
        danger: true,
        onClick: () => onDelete && onDelete({ node }),
      },
    );

    return items;
  };

  const onBodyContextMenu = (event) => {
    event.preventDefault();
    setMenu({
      x: event.clientX,
      y: event.clientY,
      node: { type: 'folder', path: '', name: '' },
    });
  };

  const startResize = (event) => {
    if (!onExplorerWidthChange) return;
    event.preventDefault();
    const startX = event.clientX;
    const startWidth = width;
    const move = (pointerEvent) => {
      onExplorerWidthChange(startWidth + (pointerEvent.clientX - startX));
    };
    const stop = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', stop);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', stop);
  };

  // Default: expand top-level folders only
  const [expanded, setExpanded] = useState(() => {
    const initial = {};
    roots.forEach((n) => {
      if (n.type === 'folder') initial[n.path] = true;
    });
    return initial;
  });

  const toggleFolder = useCallback((path) => {
    setExpanded((prev) => ({ ...prev, [path]: !prev[path] }));
  }, []);

  return (
    <aside
      className="vibe-ide-sidebar d-flex flex-column border-right"
      style={{ '--vibe-explorer-width': `${width}px` }}
    >
      <div className="vibe-ide-sidebar-header px-3 py-2 border-bottom small text-uppercase text-muted d-flex align-items-center justify-content-between">
        <span>Explorer</span>
        <div className="d-flex align-items-center">
          {onCreateFile ? (
            <button
              type="button"
              className="dc-icon-btn mr-1"
              title="New file"
              onClick={() => onCreateFile({ parentPath: '' })}
            >
              +
            </button>
          ) : null}
          {onCreateFolder ? (
            <button
              type="button"
              className="dc-icon-btn mr-1"
              title="New folder"
              onClick={() => onCreateFolder({ parentPath: '' })}
            >
              📁
            </button>
          ) : null}
          {onCollapse ? (
            <button
              type="button"
              className="dc-icon-btn"
              title="Hide explorer"
              onClick={onCollapse}
            >
              <i className="wb webconnect-layout-text-sidebar" aria-hidden="true" />
            </button>
          ) : null}
        </div>
      </div>
      <div
        className="vibe-ide-sidebar-body flex-grow-1 overflow-auto"
        onContextMenu={onBodyContextMenu}
      >
        {!roots.length ? (
          <div className="p-3 text-muted small">No files found</div>
        ) : (
          roots.map((node) => (
            <TreeNode
              key={`${node.type}:${node.path}`}
              node={node}
              depth={0}
              selectedPath={selectedPath}
              onSelectFile={onSelectFile}
              expanded={expanded}
              toggleFolder={toggleFolder}
              onContextMenu={openContextMenu}
            />
          ))
        )}
      </div>
      {menu ? (
        <ExplorerContextMenu
          x={menu.x}
          y={menu.y}
          items={buildMenuItems(menu.node)}
          onClose={closeContextMenu}
        />
      ) : null}
      <button
        type="button"
        className="vibe-explorer-resizer"
        aria-label="Resize explorer"
        title="Drag to resize explorer"
        onPointerDown={startResize}
      />
    </aside>
  );
};

export default EditorSidebar;
